import api from './api';

const ensureToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }
  return token;
};

const isBrowserFile = (value) => typeof File !== 'undefined' && value instanceof File;

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const splitFilesAndUrls = (items = []) => {
  const files = [];
  const urls = [];

  items.forEach((item) => {
    if (isBrowserFile(item)) {
      files.push(item);
    } else if (typeof item === 'string') {
      const trimmed = item.trim();
      if (trimmed.length > 0) {
        urls.push(trimmed);
      }
    }
  });

  return { files, urls };
};

const convertFilesToBase64 = async (files = []) => {
  const results = [];
  for (const file of files) {
    try {
      const base64 = await toBase64(file);
      results.push(base64);
    } catch (error) {
      console.error('Base64 conversion failed for file:', file?.name, error);
    }
  }
  return results;
};

const mapDateToIso = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const getProjects = async (category = 'genel') => {
  try {
    const token = ensureToken();
    const response = await api.get(`/api/Project/All?category=${category}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data ?? response;
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

export const getProjectByGuid = async (guid) => {
  try {
    const token = ensureToken();
    const response = await api.post(
      '/api/Project/GetProjectByGuid',
      { guid },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data ?? response;
  } catch (error) {
    console.error('Error fetching project:', error);
    throw error;
  }
};

export const addProject = async (data) => {
  try {
    const token = ensureToken();

    if (!data?.title) {
      throw new Error('Proje başlığı zorunludur');
    }

    let imageBase64 = '';
    if (data.image && isBrowserFile(data.image)) {
      imageBase64 = await toBase64(data.image);
    }

    const { files: beforeImageFiles } = splitFilesAndUrls(data.beforeImages);
    const { files: galleryImageFiles } = splitFilesAndUrls(data.galleryImages);
    const { files: afterImageFiles } = splitFilesAndUrls(data.afterImages);

    const videoFiles = Array.isArray(data.videoFiles) ? data.videoFiles : [];
    const beforeVideoFiles = Array.isArray(data.beforeVideoFiles) ? data.beforeVideoFiles : [];
    const afterVideoFiles = Array.isArray(data.afterVideoFiles) ? data.afterVideoFiles : [];
    const videoUrls = Array.isArray(data.videos) ? data.videos : [];

    const payload = {
      title: data.title,
      summary: data.summary ?? '',
      content: data.content ?? '',
      projectDate: mapDateToIso(data.projectDate),
      status: data.status ?? 'Devam Ediyor',
      category: data.category ?? 'genel',
      imageBase64,
      beforeImagesBase64: await convertFilesToBase64(beforeImageFiles),
      galleryImagesBase64: await convertFilesToBase64(galleryImageFiles),
      afterImagesBase64: await convertFilesToBase64(afterImageFiles),
      videosBase64: await convertFilesToBase64(videoFiles),
      beforeVideosBase64: await convertFilesToBase64(beforeVideoFiles),
      afterVideosBase64: await convertFilesToBase64(afterVideoFiles),
      videos: videoUrls,
      beforeVideos: Array.isArray(data.beforeVideos) ? data.beforeVideos : [],
      afterVideos: Array.isArray(data.afterVideos) ? data.afterVideos : []
    };

    const response = await api.post('/api/Project/AddProject', payload, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (response === true || response === 'true') {
      return { isSuccess: true, resultMessage: 'Proje başarıyla eklendi', data: true };
    }

    return response;
  } catch (error) {
    console.error('Error adding project:', error);
    throw error;
  }
};

export const updateProject = async (data) => {
  try {
    const token = ensureToken();

    if (!data?.guid) {
      throw new Error('Project ID is required');
    }

    if (!data?.title) {
      throw new Error('Proje başlığı zorunludur');
    }

    const {
      files: beforeImageFiles,
      urls: existingBeforeImageUrls = []
    } = splitFilesAndUrls(data.beforeImages);
    const {
      files: galleryImageFiles,
      urls: existingGalleryImageUrls = []
    } = splitFilesAndUrls(data.galleryImages);
    const {
      files: afterImageFiles,
      urls: existingAfterImageUrls = []
    } = splitFilesAndUrls(data.afterImages);
    const videoFiles = Array.isArray(data.videoFiles) ? data.videoFiles : [];
    const beforeVideoFiles = Array.isArray(data.beforeVideoFiles) ? data.beforeVideoFiles : [];
    const afterVideoFiles = Array.isArray(data.afterVideoFiles) ? data.afterVideoFiles : [];
    const videos = Array.isArray(data.videos) ? data.videos : [];
    const beforeVideos = Array.isArray(data.beforeVideos) ? data.beforeVideos : [];
    const afterVideos = Array.isArray(data.afterVideos) ? data.afterVideos : [];

    let imageBase64 = '';
    if (data.image && isBrowserFile(data.image)) {
      imageBase64 = await toBase64(data.image);
    }

    const payload = {
      guid: data.guid,
      title: data.title,
      summary: data.summary ?? '',
      content: data.content ?? '',
      projectDate: mapDateToIso(data.projectDate),
      status: data.status ?? 'Devam Ediyor',
      category: data.category ?? 'genel',
      imageBase64,
      beforeImagesBase64: await convertFilesToBase64(beforeImageFiles),
      galleryImagesBase64: await convertFilesToBase64(galleryImageFiles),
      afterImagesBase64: await convertFilesToBase64(afterImageFiles),
      videosBase64: await convertFilesToBase64(videoFiles),
      beforeVideosBase64: await convertFilesToBase64(beforeVideoFiles),
      afterVideosBase64: await convertFilesToBase64(afterVideoFiles),
      videos,
      beforeVideos,
      afterVideos,
      existingBeforeImageUrls,
      existingGalleryImageUrls,
      existingAfterImageUrls,
      existingVideoUrls: Array.isArray(data.existingVideoUrls) ? data.existingVideoUrls : [],
      existingBeforeVideoUrls: Array.isArray(data.existingBeforeVideoUrls)
        ? data.existingBeforeVideoUrls
        : [],
      existingAfterVideoUrls: Array.isArray(data.existingAfterVideoUrls)
        ? data.existingAfterVideoUrls
        : []
    };

    const response = await api.post('/api/Project/UpdateProject', payload, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (response === true || response === 'true') {
      return { isSuccess: true, resultMessage: 'Proje başarıyla güncellendi', data: true };
    }

    return response;
  } catch (error) {
    console.error('Error updating project:', error);
    return { isSuccess: false, resultMessage: error.message };
  }
};

export const deleteProject = async (guid) => {
  try {
    const token = ensureToken();

    if (!guid) {
      throw new Error('Project ID is required');
    }

    const response = await api.post(
      '/api/Project/DeleteProject',
      { guid },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (response === true || response === 'true') {
      return { isSuccess: true, resultMessage: 'Proje başarıyla silindi', data: true };
    }

    return response;
  } catch (error) {
    console.error('Error deleting project:', error);
    return { isSuccess: false, resultMessage: error.message };
  }
};
